import React from 'react';
import { Controller } from 'react-hook-form';
import { WorkflowComponentProps } from '../../../../Workflow/Workflow.meta';
import { Box } from '../../../../CommonComponents/OPL/Box/Box';
import { Button } from '../../../../CommonComponents/OPL/Button/Button';
import { Text } from '../../../../CommonComponents/OPL/Text/Text';
import { HtmlTable } from '../../../../CommonComponents/OPL/HtmlTable/HtmlTable';
import { formatCurrency } from '../../../../CommonUtils/formatNumber';
import { FIXED_EXPENSES, FREQUENCY_OPTIONS, getNewExpense, HEADERS } from './ExpensesDetailed.meta';
import { Select } from '../../../../CommonComponents/OPL/Select/Select/Select';
import { InputNumberMask } from '../../../../CommonComponents/OPL/Input/InputNumberMask';
import { FORM_ID } from '../../Expenses.meta';
import { Label } from '../../../../CommonComponents/OPL/Label/Label';
import { useExpensesDetailed } from './useExpensesDetailed';
import { Alert } from '../../../../CommonComponents/OPL/Alert/Alert';
import { Input } from '../../../../CommonComponents/OPL/Input/Input';
import { IconButton } from '../../../../CommonComponents/OPL/IconButton/IconButton';
import { ExpenseCategory, ExpensesDTO } from '../../api/useExpensesQuery';
import { Tooltip } from '../../../../CommonComponents/OPL/Tooltip/Tooltip';

interface Props {
  clientId: string;
  data: ExpensesDTO;
  workflowProps?: WorkflowComponentProps;
}

export const ExpensesDetailed = ({ clientId, data, workflowProps }: Props) => {
  const {
    formBag,
    expensesFieldArray,
    recurrenceYearsOptions,
    onSubmit,
    estimateExpenses,
    saveExpensesMutation,
    estimateExpensesMutation,
    estimateExpenseCategory,
    recurrenceSetActions,
    categoryEstimatingSetActions,
  } = useExpensesDetailed({
    clientId,
    data,
    workflowProps,
  });

  return (
    <>
      {data.CanEstimate && (
        <Box css="grid-area: header">
          <Button
            variant="secondary"
            leftIcon="fas fa-magic"
            onClick={estimateExpenses}
            isLoading={estimateExpensesMutation.isLoading}
          >
            Estimate from Transactions
          </Button>
        </Box>
      )}

      <Box css="grid-area: content" mt="24">
        {saveExpensesMutation.isError && (
          <Alert
            message={saveExpensesMutation.error.message}
            onRequestClose={saveExpensesMutation.reset}
            variant="error"
            mb="16"
          />
        )}

        <form id={FORM_ID} onSubmit={onSubmit} aria-label="Detailed expenses form">
          <HtmlTable css="overflow-y: unset" variant={workflowProps && 'bordered'}>
            <table>
              <colgroup>
                <col />
                <col width="90px" />
                <col />
                <col width="186px" />
                <col width="90px" />
              </colgroup>

              <caption>
                <Box display="flex" alignItems="center" justifyContent="space-between">
                  <span>Expense Details</span>

                  <Button
                    leftIcon="fas fa-plus"
                    variant="subtleActive"
                    onClick={() => expensesFieldArray.append(getNewExpense())}
                  >
                    Add Expense
                  </Button>
                </Box>
              </caption>

              <thead>
                <tr>
                  {HEADERS.map(({ name, key }) => (
                    <th key={key} aria-label={key}>
                      <Text variant="smallCaps" textAlign="left">
                        {name}
                      </Text>
                    </th>
                  ))}
                </tr>
              </thead>

              <tbody>
                {FIXED_EXPENSES.map(({ name, key }) => {
                  const value = data[key];

                  if (value > 0) {
                    return (
                      <tr key={key}>
                        <td>
                          <Text fontWeight={500}>{name}</Text>
                        </td>
                        <td aria-label="Empty Cell">&nbsp;</td>
                        <td>
                          <Text fontWeight={500} variant="tabular">
                            {formatCurrency(value)}
                          </Text>
                        </td>
                        <td>
                          <Text fontWeight={500}>Monthly</Text>
                        </td>
                        <td aria-label="Empty Cell">&nbsp;</td>
                      </tr>
                    );
                  }

                  return null;
                })}

                {expensesFieldArray.fields.map((field, index) => {
                  if (field.ExpenseOrigin === 'PulseCheck') {
                    return (
                      <tr key={field.key}>
                        <td>
                          <Text fontWeight={500}>{field.Name}</Text>
                        </td>
                        <td aria-label="Empty Cell">&nbsp;</td>
                        <td>
                          <Text fontWeight={500}>{formatCurrency(field.RecurringAmount)}</Text>
                        </td>
                        <td>
                          <Text fontWeight={500}>{field.Cadence}</Text>
                        </td>
                        <td aria-label="Empty Cell">&nbsp;</td>
                      </tr>
                    );
                  }

                  return (
                    <React.Fragment key={field.key}>
                      <tr
                        style={recurrenceSetActions.has(field.Id) ? { borderBottom: 'none' } : {}}
                      >
                        <td>
                          {field.isCustom ? (
                            <Input
                              {...formBag.register(`expenses.${index}.Name`)}
                              placeholder="Expense name"
                              aria-label="Expense name"
                            />
                          ) : (
                            <Text fontWeight={500}>{field.Name}</Text>
                          )}
                        </td>
                        <td>
                          {data.CanEstimate && !field.isCustom && (
                            <Tooltip
                              content="Estimate your expenses for this category based on historical data available from your linked accounts."
                              delay={[1000, 0]}
                            >
                              <IconButton
                                icon="far fa-magic"
                                onClick={() =>
                                  estimateExpenseCategory(field.Name as ExpenseCategory)
                                }
                                isLoading={categoryEstimatingSetActions.has(
                                  field.Name as ExpenseCategory,
                                )}
                                aria-label="Estimate expense"
                                variant="subtleInactive"
                              />
                            </Tooltip>
                          )}
                        </td>
                        <td>
                          <Controller
                            control={formBag.control}
                            name={`expenses.${index}.Amount`}
                            render={({ field: { value, onChange } }) => (
                              <InputNumberMask
                                value={value}
                                allowNegative={false}
                                aria-label="Amount"
                                leftIcon="far fa-dollar-sign"
                                decimalScale={2}
                                onValueChange={({ floatValue }) => onChange(floatValue ?? null)}
                                css="max-width: 320px"
                              />
                            )}
                          />
                        </td>
                        <td>
                          <Controller
                            name={`expenses.${index}.Frequency`}
                            control={formBag.control}
                            render={({ field: { onChange, value } }) => (
                              <Select
                                aria-label={`${field.Name} expense period selector`}
                                value={FREQUENCY_OPTIONS.find(option => option.value === value)}
                                options={FREQUENCY_OPTIONS}
                                onChange={option => {
                                  onChange(option?.value);

                                  if (option?.value === null) {
                                    recurrenceSetActions.add(field.Id);
                                  } else {
                                    recurrenceSetActions.remove(field.Id);
                                    formBag.setValue(`expenses.${index}.Year`, null);
                                    formBag.setValue(`expenses.${index}.RepeatEveryNYears`, null);
                                    formBag.setValue(`expenses.${index}.RepeatForNYears`, null);
                                  }
                                }}
                              />
                            )}
                          />
                        </td>
                        <td>
                          {field.isCustom && (
                            <IconButton
                              icon="far fa-minus-circle"
                              variant="subtleInactive"
                              onClick={() => expensesFieldArray.remove(index)}
                              aria-label="Remove expense"
                            />
                          )}
                        </td>
                      </tr>

                      {recurrenceSetActions.has(field.Id) && (
                        <tr>
                          <td colSpan={HEADERS.length}>
                            <Box display="flex" alignItems="center">
                              <Label htmlFor={`select--expenses.${index}.Year`} mr="8">
                                Starting
                              </Label>
                              <Controller
                                name={`expenses.${index}.Year`}
                                control={formBag.control}
                                render={({ field: { onChange, value } }) => (
                                  <Select
                                    id={`expenses.${index}.Year`}
                                    aria-label={`${field.Name} expense period selector`}
                                    value={recurrenceYearsOptions.find(opt => opt.value === value)}
                                    options={recurrenceYearsOptions}
                                    onChange={option => onChange(option?.value)}
                                    css="min-width: 160px"
                                  />
                                )}
                              />

                              <Label htmlFor={`input--expenses.${index}.RepeatEveryNYears`} mx="8">
                                Every
                              </Label>
                              <Controller
                                control={formBag.control}
                                name={`expenses.${index}.RepeatEveryNYears`}
                                render={({ field: { value, onChange } }) => (
                                  <InputNumberMask
                                    id={`expenses.${index}.RepeatEveryNYears`}
                                    value={value}
                                    allowNegative={false}
                                    onValueChange={({ floatValue }) => onChange(floatValue ?? null)}
                                    css="max-width: 140px"
                                  />
                                )}
                              />

                              <Label htmlFor={`input--expenses.${index}.RepeatForNYears`} mx="8">
                                year(s) for
                              </Label>
                              <Controller
                                control={formBag.control}
                                name={`expenses.${index}.RepeatForNYears`}
                                render={({ field: { value, onChange } }) => (
                                  <InputNumberMask
                                    id={`expenses.${index}.RepeatForNYears`}
                                    value={value}
                                    allowNegative={false}
                                    onValueChange={({ floatValue }) => onChange(floatValue ?? null)}
                                    css="max-width: 140px"
                                  />
                                )}
                              />

                              <Text variant="formLabel" ml="8">
                                year(s)
                              </Text>
                            </Box>
                          </td>
                        </tr>
                      )}
                    </React.Fragment>
                  );
                })}
              </tbody>
            </table>
          </HtmlTable>
        </form>
      </Box>
    </>
  );
};
